import gsap from 'gsap'
import MorphSVGPlugin from 'gsap/MorphSVGPlugin'
import ScrollTrigger from 'gsap/ScrollTrigger'
import SplitText from 'gsap/SplitText'
import { getOperatingSystem } from '../utils/get-operating-system'
import { lenisInstance, LenisScroll } from '../utils/lenis-scroll'
import { debounce, throttle } from 'throttle-debounce'

const PaskaPage1 = {
  breakpoints: {
    isDesktop: `(min-width: 768px)`,
    isMobile: `(max-width: 767px)`,
  },

  debug: false,
  deferredHashSelector: null,
  deferredHashValue: null,
  isIntroFinished: false,
  hasHandledDeferredHash: false,

  init(): void {
    if (!document.querySelector('.paska-page.paska-page--1')) {
      return
    }

    const start = () => {
      this.prepareDeferredHashScroll()

      gsap.registerPlugin(MorphSVGPlugin, ScrollTrigger, SplitText)
      LenisScroll.init()

      //
      ;(async () => {
        await document.fonts.ready
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))

        new SplitText('.pp-title__text, .pp-details__title, .pp-products__card-title, .pp-button span', {
          aria: 'hidden',
          // linesClass: 'split-line',
          // wordsClass: 'split-word',
          charsClass: 'split-char',
          // mask: 'lines',
          tag: 'i',
          // type: 'lines, words, chars',
          type: 'chars, words',
        })

        this.handleAnimations()
        this.handleButtonAndCardHovers()
        this.handleCardHovers()
        this.handleParallaxes()
        this.handleScrollUp()
        ScrollTrigger.refresh()
      })()
    }

    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => {
          start()
        },
        { once: true },
      )
    } else {
      start()
    }

    window.addEventListener(
      'load',
      () => {
        ScrollTrigger.refresh()
      },
      { once: true },
    )
  },

  handleAnimations(): void {
    // Chocolate reveal + stdDeviation handling for gooey effect

    const lines = document.querySelectorAll('.pp-cover__bottom-lines > div')
    const chocolateDelay = 0.21

    gsap.matchMedia().add(this.breakpoints, (context) => {
      const { isDesktop } = context.conditions

      gsap
        //
        .timeline({ defaults: { duration: 3.2, ease: 'power4.inOut' } })
        .timeScale(this.debug ? 12 : 1)
        .set('.pp-cover', { willChange: 'transform' })
        .set(lines, { willChange: 'transform' })
        .to(
          '.pp-cover',
          {
            clearProps: 'willChange',
            onComplete: () => {
              gsap.set('.pp-cover', { autoAlpha: 0 })
            },
            yPercent: isDesktop ? -130 : -107,
          },
          chocolateDelay,
        )
        .to('.pp-cover__bottom svg:first-of-type path', { morphSVG: '.pp-cover__bottom svg:last-of-type path' }, chocolateDelay)
        .to([lines[0], lines[2]], { clearProps: 'willChange', y: '0%' }, chocolateDelay)
        .to([lines[1]], { clearProps: 'willChange', y: '-50%' }, chocolateDelay)
        .to([lines[3], lines[4]], { clearProps: 'willChange', y: '-10%' }, chocolateDelay)
        .set('.pp-header, .pp-hero, .pp-hero-chicken, .pp-details, .pp-details-chicken, .pp-products, .pp-footer', { visibility: 'visible' }, chocolateDelay)
    })

    let lastWidth = window.innerWidth

    const updateStdDeviation = () => {
      const width = window.innerWidth

      if (width === lastWidth) return
      lastWidth = width

      const minStd = 1
      const midStd = 10
      const maxStd = 18

      let stdDeviation

      if (width <= 768) {
        const progress = Math.max(0, width / 768)
        stdDeviation = minStd + (midStd - minStd) * progress
      } else {
        const progress = Math.min(1, (width - 768) / (1800 - 768))
        stdDeviation = midStd + (maxStd - midStd) * progress
      }

      const filter = document.querySelector('#goo feGaussianBlur')
      filter?.setAttribute('stdDeviation', stdDeviation.toFixed(2))
    }

    updateStdDeviation()
    window.addEventListener('resize', updateStdDeviation)

    // Reveal hero stuff + chicken

    const heroRevealStart = 2.4 // 1.2 // 0.44

    lenisInstance && !this.debug && lenisInstance.stop()

    setTimeout(
      () => {
        lenisInstance && lenisInstance.start()
        this.isIntroFinished = true
        this.runDeferredHashScroll()
      },
      2,
      // (heroRevealStart + 0.44) * 1000,
      // (heroRevealStart + 0.2) * 1000,
    )

    gsap.matchMedia().add(this.breakpoints, () => {
      gsap
        //
        .timeline({ defaults: { duration: 1.234, ease: 'elastic.out(1.2,0.7)' } })
        .timeScale(this.debug ? 12 : 1)
        .set('.pp-header__logo, .pp-hero__supertitle', { opacity: 0, overwrite: true, scale: 0, willChange: 'opacity, transform' })
        .set('.pp-hero__title .split-char', { opacity: 0, overwrite: true, scaleY: 0, willChange: 'opacity, transform' })
        .set('.pp-candy--hero', { overwrite: true, willChange: 'transform', yPercent: -10 })
        .set('.pp-candy--hero img', { overwrite: true, scale: 0, willChange: 'transform' })
        .set('.pp-hero__glow', { opacity: 0, overwrite: true, willChange: 'opacity' })
        .set('.pp-hero-chicken__picture-container', { opacity: 0, overwrite: true, willChange: 'opacity, transform', xPercent: -15, yPercent: 90 })
        .set('.pp-hero-chicken__glow', { overwrite: true, willChange: 'opacity', opacity: 0 })
        //
        .to('.pp-hero__title .split-char', { clearProps: 'willChange', opacity: 1, scaleY: 1, stagger: 0.03 }, heroRevealStart)
        .to('.pp-hero__supertitle', { clearProps: 'willChange', opacity: 1, scale: 1 }, '<0.3')
        .to('.pp-header__logo', { clearProps: 'willChange', opacity: 1, scale: 1 }, '<0.3')
        .set('.pp-hero-chicken__picture-container', { opacity: 1 }, heroRevealStart + 0.9)
        .to('.pp-hero-chicken__picture-container', { clearProps: 'willChange', ease: 'elastic.out(1.05,0.95)', xPercent: 0, yPercent: 0 }, heroRevealStart + 0.91)
        .to('.pp-hero-chicken__glow', { clearProps: 'willChange', duration: 3, ease: 'power2.out', opacity: 1 }, heroRevealStart + 0.4)
        .to('.pp-hero__glow', { clearProps: 'willChange', duration: 3, ease: 'power2.out', opacity: 1 }, heroRevealStart - 0.5)
        .to('.pp-candy--hero', { clearProps: 'willChange', duration: 3, ease: 'power2.out', yPercent: 0 }, heroRevealStart + 0.5)
        .to('.pp-candy--hero img', { clearProps: 'willChange', ease: 'elastic.out(1.05,0.95)', scale: 1, stagger: 0.15 }, heroRevealStart + 0.5)
    })

    // Hero chicken scrub

    gsap.set('.pp-hero-chicken__picture', { overwrite: true, rotation: 3, willChange: 'transform', xPercent: -15, yPercent: 0 })

    ScrollTrigger.create({
      animation: gsap.fromTo('.pp-hero-chicken__picture', { rotation: 3, xPercent: -15, yPercent: 0 }, { ease: 'none', rotation: -15, xPercent: 30, yPercent: 44 }),
      end: '100% 0%',
      start: '100% 100%',
      scrub: 1.234,
      trigger: '.pp-hero-chicken',
    })

    // Details section images fade & pan

    const fadeAnimation = gsap.timeline({ defaults: { ease: 'none' } })
    const fadeDuration = 1.234
    const pictures = document.querySelectorAll('.pp-details__picture-container')
    const panDuration = 7

    pictures.forEach((picture, index) => {
      gsap.set(picture, { zIndex: pictures.length - index })

      const tl = gsap
        .timeline({ defaults: { duration: panDuration, ease: 'none' } })
        .fromTo(picture, { yPercent: 0 }, { yPercent: -33.333 })
        .to(picture, { duration: fadeDuration, opacity: 0 }, `>-${fadeDuration}`)

      fadeAnimation.add(tl, index === 0 ? 0 : `-=${fadeDuration}`)

      if (index === pictures.length - 1) {
        fadeAnimation.add(() => {
          fadeAnimation.seek(panDuration - fadeDuration).play()
        }, `>-${fadeDuration}`)
      }
    })

    // Details section title + text animation on scroll

    gsap.matchMedia().add(this.breakpoints, (context) => {
      const { isDesktop } = context.conditions

      gsap.set('.pp-details__title .split-char', { opacity: 0, overwrite: true, scaleY: 0, willChange: 'opacity, transform' })

      ScrollTrigger.create({
        onEnter: () => {
          gsap.to('.pp-details__title .split-char', { clearProps: 'willChange', duration: 1.234, ease: 'elastic.out(1.2,0.7)', opacity: 1, scaleY: 1, stagger: 0.03 })
        },
        start: isDesktop ? '0% 85%' : '0% 95%',
        trigger: '.pp-details__title',
      })

      ScrollTrigger.create({
        onLeaveBack: () => {
          gsap.set('.pp-details__title .split-char', { opacity: 0, overwrite: true, scaleY: 0, willChange: 'transform' })
        },
        start: '0% 100%',
        trigger: '.pp-details__title',
      })

      document.querySelectorAll('.pp-details__text p').forEach((item) => {
        gsap.set(item, { opacity: 0, overwrite: true })

        ScrollTrigger.create({
          animation: gsap.fromTo(item,  { opacity: 0 }, { ease: 'none', opacity: 1 }),
          end: '+=10%',
          scrub: 1.234,
          start: isDesktop ? '0% 85%' : '0% 95%',
          trigger: item,
        })
      })
    })

    // Details section chicken scrub

    gsap.matchMedia().add(this.breakpoints, (context) => {
      const { isDesktop } = context.conditions
        gsap.set('.pp-details-chicken__picture', { overwrite: true, willChange: 'transform', xPercent: isDesktop ? -15 : 0, yPercent: 60 })

        ScrollTrigger.create({
          animation: gsap.fromTo('.pp-details-chicken__picture', { xPercent: isDesktop ? -15 : 0, yPercent: 60 }, { ease: 'none', xPercent: isDesktop ? 0 : 15, yPercent: isDesktop ? 13 : 3 }),
          end: isDesktop ? '100% 25%' : '100% 50%',
          start: '0% 120%',
          scrub: 1.234,
          trigger: '.pp-details-chicken',
        })
      })

    // Info section fade in on scroll

    // gsap.set('.pp-info__inner', { opacity: 0, y: '123rem', overwrite: true })

    // ScrollTrigger.create({
    //   onEnter: () => {
    //     gsap.to('.pp-info__inner', { duration: 1.23, ease: 'power3.out', opacity: 1, y: '0rem' })
    //   },
    //   onUpdate: () => {
    //     if (ScrollTrigger.isInViewport('.pp-info__inner')) {
    //       const infoTextItemTriggers = ScrollTrigger.getAll().filter((s) => s.vars.id === 'info-text-item')

    //       infoTextItemTriggers.forEach((trigger) => {
    //         trigger.refresh()
    //       })
    //     }
    //   },
    //   start: '0% 80%',
    //   trigger: '.pp-info',
    // })

    // ScrollTrigger.create({
    //   onLeaveBack: () => {
    //     gsap.set('.pp-info__inner', { opacity: 0, y: '123rem', overwrite: true })
    //   },
    //   start: '0% 100%',
    //   trigger: '.pp-info',
    // })

    // Info text list items animation on scroll

    // document.querySelectorAll('.pp-info__text > p').forEach((item, index) => {
    //   gsap.set(item, { opacity: 0, overwrite: true })

    //   ScrollTrigger.create({
    //     id: 'info-text-item',
    //     onEnter: () => {
    //       gsap.to(item, { duration: 1.234, ease: 'power3.out', opacity: 1, delay: index * 0.1 })
    //     },
    //     start: '0% 90%',
    //     trigger: item,
    //   })

    //   ScrollTrigger.create({
    //     id: 'info-text-item',
    //     onLeaveBack: () => {
    //       gsap.set(item, { opacity: 0, overwrite: true })
    //     },
    //     start: '0% 100%',
    //     trigger: item,
    //   })
    // })

    // document.querySelectorAll('.pp-info__text li').forEach((item, index) => {
    //   item.insertAdjacentHTML('afterbegin', '<div><div></div></div>')

    //   gsap.set(item, { opacity: 0, overwrite: true, x: '44rem' })
    //   gsap.set(item.firstElementChild, { overwrite: true, scale: 5, x: '-44rem' })

    //   ScrollTrigger.create({
    //     id: 'info-text-item',
    //     onEnter: () => {
    //       gsap.to(item, { delay: index * 0.1, duration: 1.234, ease: 'power3.out', opacity: 1, x: 0 })
    //       gsap.to(item.firstElementChild, { delay: index * 0.1, duration: 1.234, ease: 'power3.out', scale: 1, x: 0 })
    //     },
    //     start: '0% 90%',
    //     trigger: item,
    //   })

    //   ScrollTrigger.create({
    //     id: 'info-text-item',
    //     onLeaveBack: () => {
    //       gsap.set(item, { opacity: 0, overwrite: true, x: '44rem' })
    //       gsap.set(item.firstElementChild, { overwrite: true, scale: 5, x: '-44rem' })
    //     },
    //     start: '0% 100%',
    //     trigger: item,
    //   })
    // })

    // Info image zoom in on scroll

    // gsap.matchMedia().add(this.breakpoints, (context) => {
    //   const { isDesktop } = context.conditions

    //   ScrollTrigger.create({
    //     animation: gsap.fromTo('.pp-info__picture', { scale: isDesktop ? 1.66 : 2.66 }, { ease: 'none', scale: 1 }),
    //     end: isDesktop ? '85% 100%' : '100% 100%',
    //     start: '0% 100%',
    //     scrub: 1.21,
    //     trigger: '.pp-info',
    //   })
    // })

    // Products title animation on scroll

    gsap.matchMedia().add(this.breakpoints, (context) => {
      const { isDesktop } = context.conditions

      gsap.set('.pp-products__title .split-char', { opacity: 0, overwrite: true, scaleY: 0, willChange: 'opacity, transform' })

      ScrollTrigger.create({
        onEnter: () => {
          gsap.to('.pp-products__title .split-char', { clearProps: 'willChange', duration: 1.234, ease: 'elastic.out(1.2,0.7)', opacity: 1, scaleY: 1, stagger: 0.03 })
        },
        start: isDesktop ? '0% 85%' : '0% 95%',
        trigger: '.pp-products__title',
      })

      ScrollTrigger.create({
        onLeaveBack: () => {
          gsap.set('.pp-products__title .split-char', { opacity: 0, overwrite: true, scaleY: 0, willChange: 'transform' })
        },
        start: '0% 100%',
        trigger: '.pp-products__title',
      })
    })

    // Products cards animation on scroll

    gsap.matchMedia().add(this.breakpoints, (context) => {
      const { isDesktop } = context.conditions

      document.querySelectorAll('.pp-products__card-inner').forEach((card, index) => {
        gsap.set(card, { opacity: 0, overwrite: true, willChange: 'opacity, transform', y: '123rem' })

        ScrollTrigger.create({
          onEnter: () => {
            const delay = isDesktop ? (index % 3) * 0.1 : (index % 2) * 0.1
            gsap.to(card, { clearProps: 'willChange', delay, duration: 1.234, ease: isDesktop ? 'elastic.out(1,0.9)' : 'power4.out', opacity: 1, y: '0rem' })
          },
          start: isDesktop ? '0% 85%' : '0% 95%',
          trigger: card.parentElement,
        })

        ScrollTrigger.create({
          onLeaveBack: () => {
            gsap.set(card, { opacity: 0, overwrite: true, willChange: 'opacity, transform', y: '123rem' })
          },
          start: '0% 100%',
          trigger: card.parentElement,
        })
      })
    })

    // Products chocolate

    // gsap.matchMedia().add(this.breakpoints, () => {
    ScrollTrigger.create({
      animation: gsap.to('.pp-products__chocolate div', { ease: 'none', y: '0%' }),
      end: '+=50%',
      scrub: 1.234,
      start: '100% 85%',
      trigger: '.pp-products',
    })
    // })

    // Footer title animation on scroll

    gsap.matchMedia().add(this.breakpoints, (context) => {
      const { isDesktop } = context.conditions

      gsap.set('.pp-footer__title .split-char', { opacity: 0, overwrite: true, scaleY: 0, willChange: 'opacity, transform' })
      gsap.set('.pp-footer__glow', { opacity: 0, overwrite: true, willChange: 'opacity' })

      ScrollTrigger.create({
        onEnter: () => {
          gsap.to('.pp-footer__title .split-char', { clearProps: 'willChange', duration: 1.234, ease: 'elastic.out(1.2,0.7)', opacity: 1, scaleY: 1, stagger: 0.03 })
          gsap.to('.pp-footer__glow', { clearProps: 'willChange', duration: 3, ease: 'power2.out', opacity: 1 })
        },
        start: isDesktop ? '0% 85%' : '0% 95%',
        trigger: '.pp-footer__title',
      })

      ScrollTrigger.create({
        onLeaveBack: () => {
          gsap.set('.pp-footer__title .split-char', { opacity: 0, overwrite: true, scaleY: 0, willChange: 'opacity, transform' })
        },
        start: '0% 100%',
        trigger: '.pp-footer__title',
      })

      ScrollTrigger.create({
        onLeaveBack: () => {
          gsap.set('.pp-footer__glow', { opacity: 0, overwrite: true, willChange: 'opacity' })
        },
        start: '0% 100%',
        trigger: '.pp-footer',
      })
    })

    // Footer buttons animation on scroll

    gsap.matchMedia().add(this.breakpoints, (context) => {
      const { isDesktop } = context.conditions

      gsap.set('.pp-footer__buttons .pp-button', { opacity: 0, overwrite: true, scale: 0 })

      ScrollTrigger.create({
        onEnter: () => {
          gsap.to('.pp-footer__buttons .pp-button', { duration: 1.234, ease: 'elastic.out(1.05,0.95)', opacity: 1, scale: 1, stagger: 0.21 })
        },
        start: isDesktop ? '0% 85%' : '0% 95%',
        trigger: '.pp-footer__buttons',
      })

      ScrollTrigger.create({
        onLeaveBack: () => {
          gsap.set('.pp-footer__buttons .pp-button', { opacity: 0, overwrite: true, scale: 0 })
        },
        start: '0% 100%',
        trigger: '.pp-footer__buttons',
      })
    })

    // Footer chicken scrub

    gsap.set('.pp-footer__chicken-picture', { overwrite: true, rotation: 3, willChange: 'transform', xPercent: 21, yPercent: 21 })

    ScrollTrigger.create({
      animation: gsap.fromTo('.pp-footer__chicken-picture', { rotation: 3, xPercent: 21, yPercent: 21 }, { ease: 'none', rotation: 12, xPercent: -12, yPercent: 0 }),
      end: '100% 100%',
      start: '0% 100%',
      scrub: 1.234,
      trigger: '.pp-footer__chicken',
    })
  },

  handleButtonAndCardHovers(): void {
    if (getOperatingSystem() !== 'desktop') return

    document.querySelectorAll('.pp-products__card').forEach((card) => {
      card.addEventListener('mouseenter', () => {
        gsap
          //
          .timeline()
          .to(card.querySelectorAll('.split-char'), { duration: 0.1, ease: 'none', scaleY: 0.3, stagger: 0.01 })
          .to(card.querySelectorAll('.split-char'), { duration: 1.234, ease: 'elastic.out(1.2,0.7)', scaleY: 1, stagger: 0.01 }, '<0.1')
      })
    })

    document.querySelectorAll('.pp-button').forEach((button) => {
      button.addEventListener('mouseenter', () => {
        gsap
          //
          .timeline()
          .to(button.querySelectorAll('.split-char'), { duration: 0.1, ease: 'none', scaleY: 0.5, stagger: 0.03 })
          .to(button.querySelectorAll('.split-char'), { duration: 1.234, ease: 'elastic.out(1.2,0.7)', scaleY: 1, stagger: 0.03 }, '<0.1')
      })
    })
  },

  handleCardHovers(): void {
    if (getOperatingSystem() !== 'desktop') return

    // Moving product cards

    const commonProps = {
      duration: 1.23,
      ease: 'power3.out',
      overwrite: true,
    }

    const moveTheCard = (event, card, rotationModifier = 0.12) => {
      const rect = card.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      const xPercent = (x / rect.width) * 100 - 50
      const yPercent = (y / rect.height) * 100 - 50

      gsap.to(card, {
        ...commonProps,
        rotateX: -yPercent * rotationModifier,
        rotateY: xPercent * rotationModifier,
      })

      gsap.to(card.querySelector('img'), {
        ...commonProps,
        x: `${xPercent / -6.3}rem`,
        y: `${yPercent / -4.2}rem`,
      })
    }

    document.querySelectorAll('.pp-products__card-inner-2').forEach((card) => {
      card.addEventListener(
        'mouseleave',
        debounce(70, () => {
          gsap.to(card, { ...commonProps, rotateX: 0, rotateY: 0 })
          gsap.to(card.querySelector('img'), { ...commonProps, x: '0rem', y: '0rem' })
        }),
      )

      card.addEventListener(
        'mousemove',
        throttle(60, (e: MouseEvent) => {
          moveTheCard(e, card)
        }),
      )
    })

    // // Moving info & details card

    // document.body.addEventListener(
    //   'mousemove',
    //   throttle(60, (e: MouseEvent) => {
    //     const x = e.clientX
    //     const y = e.clientY
    //     const xPercent = (x / window.innerWidth) * 100 - 50
    //     const yPercent = (y / window.innerHeight) * 100 - 50
    //     const rotationModifier = 0.021

    //     // gsap.to('.pp-details__inner-2, .pp-info__inner-2', {
    //     gsap.to('.pp-details__inner-2', {
    //       ...commonProps,
    //       rotateX: -yPercent * rotationModifier,
    //       rotateY: xPercent * rotationModifier,
    //     })

    //     gsap.to('.pp-details__pictures', {
    //     // gsap.to('.pp-details__picture img', {
    //       ...commonProps,
    //       duration: 2.1,
    //       x: `${xPercent / 1.7}rem`,
    //     })

    //     // gsap.to('.pp-info__picture img', {
    //     //   ...commonProps,
    //     //   duration: 2.1,
    //     //   x: `${xPercent / 3.2}rem`,
    //     // })
    //   }),
    // )
  },

  handleParallaxes(): void {
    if (!document.querySelector('.pp-candy__image')) return
    // Set parallax amounts

    const setParallaxAndScale = (block, child, parallax, rotation, scale) => {
      const candy = document.querySelector<HTMLElement>(`.pp-candy--${block} .pp-candy__image:nth-child(${child})`)
      candy && (candy.dataset.parallax = parallax)
      gsap.set(candy.firstElementChild, { rotation, scale })
    }

    setParallaxAndScale('hero', 1, 121, 42, 1)
    setParallaxAndScale('hero', 2, 90, 21, 0.8)
    setParallaxAndScale('hero', 3, 160, -21, 1)

    setParallaxAndScale('products-1', 1, 121, 0, 1)
    setParallaxAndScale('products-1', 2, 210, 33, 1)
    setParallaxAndScale('products-2', 1, 121, -33, 0.8)
    setParallaxAndScale('products-2', 2, 210, 0, 0.9)
    setParallaxAndScale('products-2', 3, 121, 0, 1)
    setParallaxAndScale('products-2', 4, 150, 33, 0.8)

    setParallaxAndScale('footer', 1, 180, -21, 1)
    setParallaxAndScale('footer', 2, 121, 27, 0.8)
    setParallaxAndScale('footer', 3, 210, 0, 1)
    // Handle animation on scroll

    const candies = document.querySelectorAll<HTMLElement>('.pp-candy__image')

    // candies.forEach((candy) => {
    //   gsap.set(candy.firstElementChild, {
    //     scale: gsap.utils.random(0.8, 1, 0.01),
    //   })
    // })

    gsap.matchMedia().add(this.breakpoints, (context) => {
      const { isDesktop } = context.conditions

      candies.forEach((candy) => {
        const parallax = candy.dataset.parallax ? parseInt(candy.dataset.parallax, 10) : 121
        const rotation = gsap.utils.random(6, 12, 1)
        const rotationDirection = gsap.utils.random(0, 1, 1) ? 1 : -1
        const rotationModifier = isDesktop ? 1 : 2.1

        ScrollTrigger.create({
          animation: gsap.fromTo(
            candy,
            {
              rotation: -rotation * rotationDirection * rotationModifier,
            },
            {
              ease: 'none',
              rotation: rotation * rotationDirection * rotationModifier,
              yPercent: -parallax,
            },
          ),
          end: '100% 0%',
          scrub: parallax / 100, // 1.21,
          start: '0% 100%',
          trigger: candy,
        })
      })
    })

    // Handle mousemove

    document.body.addEventListener(
      'mousemove',
      throttle(60, (e: MouseEvent) => {
        if (getOperatingSystem() !== 'desktop') return

        const x = e.clientX
        const y = e.clientY
        const xPercent = (x / window.innerWidth) * 100 - 50
        const yPercent = (y / window.innerHeight) * 100 - 50

        candies.forEach((candy) => {
          const transformModifier = gsap.utils.random(2.5, 3.5, 0.1)

          gsap.to(candy.querySelector('picture'), {
            duration: 3.45,
            ease: 'power3.out',
            overwrite: true,
            rotation: -xPercent / 10,
            x: `${-xPercent / transformModifier}rem`,
            y: `${-yPercent / transformModifier}rem`,
          })
        })
      }),
    )
  },

  handleScrollUp(): void {
    document.querySelector('.pp-button--scroll-up').addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    })
  },

  prepareDeferredHashScroll(): void {
    const hash = window.location.hash

    if (!hash) return

    const selector = decodeURIComponent(hash)
    const target = document.querySelector(selector)

    if (!target) return

    this.deferredHashSelector = selector
    this.deferredHashValue = hash
    this.hasHandledDeferredHash = false

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }

    if (history.replaceState) {
      history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}`)
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  },

  runDeferredHashScroll(): void {
    if (!this.isIntroFinished || this.hasHandledDeferredHash || !this.deferredHashSelector) return

    const target = document.querySelector(this.deferredHashSelector)

    if (!target) return

    this.hasHandledDeferredHash = true

    requestAnimationFrame(() => {
      ScrollTrigger.refresh()

      const rect = target.getBoundingClientRect()
      const currentScroll = window.scrollY || window.pageYOffset
      const rawCenteredTop = currentScroll + rect.top - window.innerHeight / 2 + rect.height / 2
      const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
      const centeredTop = Math.min(Math.max(0, rawCenteredTop), maxScrollTop)

      if (lenisInstance) {
        lenisInstance.scrollTo(centeredTop, {
          duration: 1.234,
          immediate: false,
          lock: false,
          onComplete: () => {
            if (this.deferredHashValue && history.replaceState) {
              history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}${this.deferredHashValue}`)
            }
          },
        })
      } else {
        window.scrollTo({ top: centeredTop, behavior: 'smooth' })

        if (this.deferredHashValue && history.replaceState) {
          history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}${this.deferredHashValue}`)
        }
      }
    })
  },
}

export default PaskaPage1
